<template>
  <v-card :color="getColor(report.report_category)" class="timeline-card mb-2">
    <v-card-title class="text-white d-flex align-center pb-2">
      <div class="d-flex align-center">
        <UserChip :user="user" />
        <v-chip
          pill
          size="small"
          class="ml-1"
          variant="elevated"
          :color="getChipColor()"
          :style="{
            color: getChipTextColor(report.report_category) + ' !important',
          }"
        >
          {{ report.date_short }} @
          {{ report.time }}
        </v-chip>
      </div>
      <v-spacer></v-spacer>
      <!-- Sticky button (if applicable) -->
      <v-btn
        v-if="!hideButtons && report.report_category == 'CAS'"
        :icon="sticky_icon(report)"
        :color="report.sticky ? 'red' : 'grey-lighten-3'"
        variant="text"
        size="small"
        density="compact"
        class="mr-0"
        @click="toggle_sticky(report)"
      >
        <v-icon :size="report.sticky ? 20 : 18"></v-icon>
        <v-tooltip activator="parent" location="top">
          {{ report.sticky ? "Remove sticky status" : "Make sticky" }}
        </v-tooltip>
      </v-btn>

      <!-- Important button -->
      <v-btn
        v-if="!hideButtons"
        :icon="report.important ? 'mdi-alert' : 'mdi-alert-outline'"
        :color="report.important ? 'red' : 'grey-lighten-3'"
        :variant="report.important ? 'flat' : 'text'"
        size="x-small"
        class="mr-1"
        :class="{ 'important-timeline-active': report.important }"
        @click="toggleImportant(report)"
      >
        <v-icon :size="report.important ? 20 : 18"></v-icon>
        <v-tooltip activator="parent" location="top">
          {{ report.important ? "Mark as not important" : "Mark as important" }}
        </v-tooltip>
      </v-btn>
    </v-card-title>
    <v-card-text ref="reportContentRef" class="bg-white pt-4">
      <ClosingBlockquotes :html_string="report.description" />
      <ReportStatus :report="report" />
      <ReportTraitGroup
        :report_id="report.id"
        :should-fetch="false"
        :preloaded-traits="formatTraitsFromTags(report.traits)"
      />
    </v-card-text>
    <v-divider v-if="!hideButtons"></v-divider>
    <v-card-actions v-if="!hideButtons" class="bg-grey-lighten-4">
      <CommentButton :item="report" commentable-type="Report" />
      <v-spacer />
      <ReportContextMenu
        v-if="report.edit || report.destroy"
        :report="report"
      />
    </v-card-actions>

    <!-- Contact Info Popup -->
    <ContactInfoPopup
      v-model="showContactPopup"
      :contact-id="selectedContactId"
      :menu-target="menuTarget"
    />
  </v-card>
</template>

<script setup>
import { onMounted, computed, ref, watch, nextTick } from "vue";
import { useReportsStoreRefactor } from "@/stores/reportsStoreRefactor";
import { useTraitsStoreRefactor } from "@/stores/traitsStoreRefactor";
import { useReportCategoryIcons } from "@/composables/useReportCategoryIcons";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import { useContactMentionPopup } from "@/composables/useContactMentionPopup";
import ReportStatus from "./ReportStatus.vue";
import ReportContextMenu from "./ReportContextMenu.vue";
import ReportTraitGroup from "./ReportTraitGroup.vue";
import ClosingBlockquotes from "../ui/ClosingBlockquotes.vue";
import CommentButton from "../comments/CommentButton.vue";
import UserChip from "../users/UserChip.vue";
import ContactInfoPopup from "../contacts/ContactInfoPopup.vue";

// Props
const props = defineProps({
  report: {
    type: Object,
    required: true,
  },
  onReportUpdated: {
    type: Function,
    default: null,
  },
  hideButtons: {
    type: Boolean,
    default: false,
  },
});

// Stores and composables
const reportsStore = useReportsStoreRefactor();
const traitsStore = useTraitsStoreRefactor();
const usersStore = useUsersStoreRefactor();

const { getCategoryColor, ensureCategoriesLoaded } = useReportCategoryIcons();
const {
  showContactPopup,
  selectedContactId,
  menuTarget,
  attachMentionClickHandlers,
} = useContactMentionPopup();

// Ref for the report content container
const reportContentRef = ref(null);

// Watch for report description changes and re-attach handlers
watch(
  () => props.report?.description,
  async () => {
    console.log("ReportCard: Description changed, re-attaching handlers");
    await nextTick();

    // Add a small delay to ensure v-html has fully rendered
    setTimeout(() => {
      if (reportContentRef.value) {
        // Get the actual DOM element
        const domElement = reportContentRef.value.$el || reportContentRef.value;
        console.log("ReportCard: Re-attaching after description change");
        attachMentionClickHandlers(domElement);
      }
    }, 100);
  },
  { immediate: false },
);

// Fetch traits and report categories when component is mounted
onMounted(async () => {
  try {
    await traitsStore.fetchAll({}, true); // Force refresh to get latest trait data
    await ensureCategoriesLoaded();
  } catch (error) {
    console.error("Error fetching traits or report categories:", error);
  }

  // Attach mention click handlers after mount
  console.log("ReportCard: Component mounted, attaching handlers");
  await nextTick();

  // Add a small delay to ensure v-html has fully rendered
  setTimeout(() => {
    if (reportContentRef.value) {
      // Get the actual DOM element (reportContentRef is a v-card-text element)
      const domElement = reportContentRef.value.$el || reportContentRef.value;
      console.log("ReportCard: DOM element found:", domElement);
      console.log("ReportCard: HTML content:", domElement.innerHTML);
      attachMentionClickHandlers(domElement);
    } else {
      console.warn("ReportCard: reportContentRef is null");
    }
  }, 100);
});

const user = computed(() => {
  const stored = usersStore.getUserById(props.report.user_id);
  if (stored) return stored;
  // Author was (soft-)deleted: fall back to the name the report carries and
  // flag it as deleted so UserChip can mark it. A truly orphaned author
  // (user_deleted false) falls through to UserChip's "Onbekend" state.
  if (props.report.user_deleted) {
    return {
      name: props.report.user_name,
      short: props.report.user_short,
      deleted: true,
    };
  }
  return null;
});

// Helper functions
const getColor = (category) => {
  return getCategoryColor(category);
};

// Get appropriate chip colors for better contrast based on report category
const getChipColor = () => {
  // Keep consistent translucent white background for all report types
  return "rgba(255, 255, 255, 0.2)";
};

// Get appropriate text color for chips based on report category
const getChipTextColor = (category) => {
  const categoryColor = getColor(category);

  // For yellow (CAS) reports, use dark text for better contrast on translucent background
  if (categoryColor === "yellow") {
    return "#3E2723"; // Dark brown - excellent contrast on yellow + translucent white
  }

  // For other categories, use white text (default)
  return "white";
};

const toggle_sticky = async (report) => {
  console.log("🟡 ReportCard: toggle_sticky called for report:", {
    id: report.id,
    current_sticky: report.sticky,
    will_become: !report.sticky,
  });

  try {
    const updatedReport = await reportsStore.update({
      ...report,
      sticky: !report.sticky,
    });
    console.log("🟡 ReportCard: Update successful, got back:", {
      id: updatedReport.id,
      sticky: updatedReport.sticky,
      description: updatedReport.description?.slice(0, 30),
    });

    if (props.onReportUpdated && updatedReport) {
      console.log("🟡 ReportCard: Calling onReportUpdated callback");
      props.onReportUpdated(updatedReport);
    } else {
      console.log("🟡 ReportCard: No callback or no updated report");
    }
  } catch (error) {
    console.error("🟡 ReportCard: Error toggling sticky status:", error);
  }
};

const toggleImportant = async (report) => {
  console.log("🔶 ReportCard: toggleImportant called for report:", {
    id: report.id,
    current_important: report.important,
    will_become: !report.important,
  });

  try {
    const updatedReport = await reportsStore.update({
      ...report,
      important: !report.important,
    });
    console.log("🔶 ReportCard: Update successful, got back:", {
      id: updatedReport.id,
      important: updatedReport.important,
      description: updatedReport.description?.slice(0, 30),
    });

    if (props.onReportUpdated && updatedReport) {
      console.log("🔶 ReportCard: Calling onReportUpdated callback");
      props.onReportUpdated(updatedReport);
    } else {
      console.log("🔶 ReportCard: No callback or no updated report");
    }
  } catch (error) {
    console.error("🔶 ReportCard: Error toggling important status:", error);
  }
};

const sticky_icon = (report) => {
  return report.sticky ? "mdi-star" : "mdi-star-outline";
};

// Format traits for ReportTraitGroup
const formatTraitsFromTags = (traits) => {
  if (!traits || !Array.isArray(traits)) return [];

  // Enrich traits with full trait data (icon, color, etc.) from traits store
  return traits.map((t) => {
    const fullTrait = traitsStore.getTraitById(t.id);
    return {
      id: t.id,
      name: t.name,
      icon: fullTrait?.icon,
      color: fullTrait?.color,
      active: fullTrait?.active,
      category: fullTrait?.category,
    };
  });
};
</script>

<style scoped>
/* 📱 MOBILE-ONLY OPTIMIZATIONS (xs breakpoint: 0-599px) */
@media (max-width: 599px) {
  /* ULTRA-COMPACT card styling for mobile */
  .timeline-card .v-card-title {
    padding: 8px 8px 5px 8px !important; /* Reduced from 8px 12px */
    font-size: 0.875rem !important;
  }

  .timeline-card .v-card-text {
    padding: 10px 8px !important; /* Reduced from 8px 12px */
  }

  .timeline-card .v-card-actions {
    padding: 3px 3px 3px 8px !important; /* Reduced from 4px 12px */
    min-height: 10px !important; /* Reduced from 40px */
  }
}
</style>
